/**
 * Cliente HTTP tipado para la API de lugo.
 * Sustituye todas las llamadas a supabase.from() / supabase.rpc().
 */

const BASE = (import.meta.env.VITE_API_URL as string) ?? '';

async function request<T>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    ...init,
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error ?? `HTTP ${res.status}`);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Drizzle devuelve columnas en camelCase; los componentes legacy esperan snake_case.
 * Normaliza los campos de imágenes/media que tienen esta discrepancia.
 */
function normalizeImageItem(item: any) {
  return {
    ...item,
    image_url: item.image_url ?? item.imageUrl,
    display_order: item.display_order ?? item.displayOrder,
    media_type: item.media_type ?? item.mediaType ?? 'image',
    municipality_id: item.municipality_id ?? item.municipalityId,
    is_hidden: item.is_hidden ?? item.isHidden,
  };
}
async function requestImages(path: string, init?: RequestInit): Promise<any[]> {
  const data = await request<any[]>(path, init);
  return (data ?? []).map(normalizeImageItem);
}

/**
 * Drizzle devuelve municipios en camelCase; los componentes (MunicipalityForm, Admin)
 * esperan snake_case. Normaliza los campos del objeto municipio.
 */
function normalizeMunicipality(m: any) {
  if (!m) return m;
  return {
    ...m,
    newsletter_url: m.newsletter_url ?? m.newsletterUrl ?? '',
    carpetania_url: m.carpetania_url ?? m.carpetaniaUrl ?? '',
    enjoy_url: m.enjoy_url ?? m.enjoyUrl ?? '',
    primary_color: m.primary_color ?? m.primaryColor ?? '#005041',
    background_color: m.background_color ?? m.backgroundColor ?? '#9EBEB3',
    heroes_data: m.heroes_data ?? m.heroesData ?? [],
    nav_buttons: m.nav_buttons ?? m.navButtons ?? [],
    bottom_buttons: m.bottom_buttons ?? m.bottomButtons ?? [],
    bottom_image_url: m.bottom_image_url ?? m.bottomImageUrl ?? null,
    carousel_random_order: m.carousel_random_order ?? m.carouselRandomOrder ?? false,
    screensaver_delay: m.screensaver_delay ?? m.screensaverDelay ?? 5000,
    screensaver_enabled: m.screensaver_enabled ?? m.screensaverEnabled ?? true,
    screensaver_timeout: m.screensaver_timeout ?? m.screensaverTimeout ?? 30000,
    proxy_blocked_domains: m.proxy_blocked_domains ?? m.proxyBlockedDomains ?? [],
  };
}

// ─── Municipalities ───────────────────────────────────────────────────────────

export const api = {
  municipalities: {
    list: () => request<any[]>('/municipalities', { cache: 'no-store' }).then(data => (data ?? []).map(normalizeMunicipality)),
    get: (id: string) => request<any>(`/municipalities/${id}`).then(normalizeMunicipality),
    create: (body: any) => request<any>('/municipalities', { method: 'POST', body: JSON.stringify(body) }).then(normalizeMunicipality),
    update: (id: string, body: any) => request<any>(`/municipalities/${id}`, { method: 'PATCH', body: JSON.stringify(body) }).then(normalizeMunicipality),
    delete: (id: string) => request<void>(`/municipalities/${id}`, { method: 'DELETE' }),
  },

  // ─── Carousel images ───────────────────────────────────────────────────────
  carouselImages: {
    list: (municipalityId: string) => requestImages(`/municipalities/${municipalityId}/carousel-images`),
    create: (municipalityId: string, body: any) =>
      request<any>(`/municipalities/${municipalityId}/carousel-images`, { method: 'POST', body: JSON.stringify(body) }),
    update: (municipalityId: string, id: string, body: any) =>
      request<any>(`/municipalities/${municipalityId}/carousel-images/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (municipalityId: string, id: string) =>
      request<void>(`/municipalities/${municipalityId}/carousel-images/${id}`, { method: 'DELETE' }),
    reorder: (municipalityId: string, ids: string[]) =>
      request<void>(`/municipalities/${municipalityId}/carousel-images/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }),
  },

  // ─── Screensaver images ────────────────────────────────────────────────────
  screensaverImages: {
    list: (municipalityId: string) => requestImages(`/municipalities/${municipalityId}/screensaver-images`),
    create: (municipalityId: string, body: any) =>
      request<any>(`/municipalities/${municipalityId}/screensaver-images`, { method: 'POST', body: JSON.stringify(body) }),
    update: (municipalityId: string, id: string, body: any) =>
      request<any>(`/municipalities/${municipalityId}/screensaver-images/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (municipalityId: string, id: string) =>
      request<void>(`/municipalities/${municipalityId}/screensaver-images/${id}`, { method: 'DELETE' }),
    reorder: (municipalityId: string, ids: string[]) =>
      request<void>(`/municipalities/${municipalityId}/screensaver-images/reorder`, { method: 'POST', body: JSON.stringify({ ids }) }),
  },

  // ─── Global screensaver images ─────────────────────────────────────────────
  globalScreensaverImages: {
    list: () => requestImages('/screensaver-images/global'),
    create: (body: any) => request<any>('/screensaver-images/global', { method: 'POST', body: JSON.stringify(body) }),
    update: (id: string, body: any) => request<any>(`/screensaver-images/global/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (id: string) => request<void>(`/screensaver-images/global/${id}`, { method: 'DELETE' }),
    reorder: (ids: string[]) =>
      request<void>('/screensaver-images/global/reorder', { method: 'POST', body: JSON.stringify({ ids }) }),
  },

  // ─── Totem logs ────────────────────────────────────────────────────────────
  totemLogs: {
    list: (params?: { municipalityIds?: string[]; limit?: number }) => {
      const qs = new URLSearchParams();
      if (params?.municipalityIds?.length) qs.set('municipalityIds', params.municipalityIds.join(','));
      if (params?.limit) qs.set('limit', String(params.limit));
      return request<any[]>(`/totem-logs?${qs}`);
    },
  },

  // ─── Admin — users ─────────────────────────────────────────────────────────
  adminUsers: {
    list: () => request<any[]>('/admin/users'),
    create: (body: { email: string; password: string; role: string; municipalityIds?: string[] }) =>
      request<any>('/admin/users', { method: 'POST', body: JSON.stringify(body) }),
    delete: (id: string) => request<void>(`/admin/users/${id}`, { method: 'DELETE' }),
    changePassword: (id: string, newPassword: string) =>
      request<void>(`/admin/users/${id}/password`, { method: 'PUT', body: JSON.stringify({ newPassword }) }),
    assignMunicipality: (id: string, municipalityId: string) =>
      request<void>(`/admin/users/${id}/municipalities`, { method: 'POST', body: JSON.stringify({ municipalityId }) }),
    removeMunicipality: (id: string, municipalityId: string) =>
      request<void>(`/admin/users/${id}/municipalities/${municipalityId}`, { method: 'DELETE' }),
  },

  // ─── Storage (MinIO) ────────────────────────────────────────────────────────
  storage: {
    /**
     * Sube un fichero al servidor y devuelve su URL pública.
     *
     * @param file   Fichero a subir
     * @param path   Ruta dentro del bucket  (e.g. "heroes/municipio-hero-0.jpg")
     * @param bucket "images" (default) | "pdfs"
     */
    upload: async (file: File, path: string, bucket: 'images' | 'pdfs' = 'images'): Promise<{ url: string }> => {
      const form = new FormData();
      form.append('file', file);
      form.append('path', path);
      if (bucket !== 'images') form.append('bucket', bucket);
      const res = await fetch(`${BASE}/storage/upload`, {
        method: 'POST',
        credentials: 'include',
        body: form, // no Content-Type header — el navegador pone el boundary automáticamente
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error ?? `HTTP ${res.status}`);
      }
      return res.json();
    },
  },

  // ─── POIs ──────────────────────────────────────────────────────────────────
  pois: {
    list: (municipalityId: string) => request<any[]>(`/municipalities/${municipalityId}/pois`),
    create: (municipalityId: string, body: any) =>
      request<any>(`/municipalities/${municipalityId}/pois`, { method: 'POST', body: JSON.stringify(body) }),
    update: (municipalityId: string, poiId: string, body: any) =>
      request<any>(`/municipalities/${municipalityId}/pois/${poiId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    delete: (municipalityId: string, poiId: string) =>
      request<void>(`/municipalities/${municipalityId}/pois/${poiId}`, { method: 'DELETE' }),
  },

  // ─── Surveys ───────────────────────────────────────────────────────────────
  surveys: {
    submit: (municipalityId: string, body: { rating: number; originType: string; postalCode?: string }) =>
      request<any>(`/municipalities/${municipalityId}/surveys`, { method: 'POST', body: JSON.stringify(body) }),
    getStats: (municipalityId: string) =>
      request<any>(`/municipalities/${municipalityId}/surveys/stats`),
    list: (municipalityId: string) =>
      request<any[]>(`/municipalities/${municipalityId}/surveys`),
  },
};
